import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Calendar,
  Clock,
  Users,
  Pill,
  Activity,
  Apple,
  Dumbbell,
  ChevronRight,
  HeartPulse,
  UserPlus,
  Repeat,
  IndianRupee,
  TrendingUp,
  Trash2,
} from "lucide-react";
import dayjs from "dayjs";
import { toast } from "sonner";
import AppointmentDetailModal from "../components/AppointmentDetailModal";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const StatCard = ({ icon: Icon, label, value, accent = "primary" }) => (
  <Card className="p-5 border-border bg-white hover:-translate-y-0.5 transition-transform">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">
          {label}
        </p>
        <p className="font-heading text-2xl sm:text-3xl font-bold mt-2 leading-tight">{value ?? "—"}</p>
      </div>
      <div className={`size-10 rounded-md grid place-items-center bg-${accent}/10 text-${accent}`}>
        <Icon className="size-5" strokeWidth={1.6} />
      </div>
    </div>
  </Card>
);

const EarningCard = ({ icon: Icon, label, value, subtext, testId }) => (
  <Card className="p-6 border-border bg-white" data-testid={testId}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">
          {label}
        </p>
        <p className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mt-2 leading-tight break-all">
          ₹{(value ?? 0).toLocaleString("en-IN")}
        </p>
        {subtext && (
          <p className="text-xs text-muted-foreground mt-1">{subtext}</p>
        )}
      </div>
      <div className="size-11 rounded-md grid place-items-center bg-primary/10 text-primary">
        <Icon className="size-5" strokeWidth={1.6} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [appts, setAppts] = useState([]);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [series, setSeries] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data))
      .catch((err) => console.error("stats load failed", err));
    api.get("/dashboard/today").then((r) => setAppts(r.data))
      .catch((err) => console.error("today load failed", err));
    api.get("/dashboard/timeseries", { params: { months: 6 } }).then((r) => setSeries(r.data))
      .catch((err) => console.error("timeseries load failed", err));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleApptClick = (a) => {
    if (a.patient_id) {
      navigate(`/patients/${a.patient_id}`);
    } else {
      setSelectedAppt(a);
    }
  };

  const handleDeleteAppt = async (e, appt) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete appointment for ${appt.patient_name || appt.name || "this booking"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/appointments/${appt.id}`);
      toast.success("Appointment deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <header>
        <p className="text-xs uppercase tracking-[0.25em] text-secondary font-bold">
          Overview
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          Today at Namya
        </h1>
        <p className="text-muted-foreground mt-2 text-sm sm:text-base">
          {dayjs().format("dddd, MMMM D, YYYY")} — your clinic at a glance.
        </p>
      </header>

      {/* Today's appointments — FIRST */}
      <section data-testid="todays-appointments-section">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-heading text-2xl font-semibold tracking-tight">
            Today&apos;s appointments
          </h2>
          <span className="text-sm text-muted-foreground">{appts.length} scheduled</span>
        </div>

        {appts.length === 0 ? (
          <Card className="p-10 text-center border-dashed border-border bg-white">
            <Calendar className="size-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.4} />
            <p className="text-muted-foreground">No appointments scheduled for today.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {appts.map((a) => (
              <button
                key={a.id}
                data-testid={`appointment-card-${a.id}`}
                onClick={() => handleApptClick(a)}
                className="text-left group"
              >
                <Card
                  className={`p-4 border-border bg-white transition-colors ${
                    a.status === "completed"
                      ? "bg-emerald-50 border-emerald-200 opacity-90"
                      : "hover:border-primary/40"
                  }`}
                  data-testid={`appointment-card-${a.id}-inner`}
                >
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div
                      className={`size-10 sm:size-12 rounded-md grid place-items-center shrink-0 ${
                        a.status === "completed"
                          ? "bg-emerald-100 text-emerald-700"
                          : a.status === "ongoing"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-primary/10 text-primary"
                      }`}
                    >
                      <Clock className="size-5" strokeWidth={1.6} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading font-semibold truncate max-w-full">
                          {a.patient_name}
                        </p>
                        <Badge variant="outline" className="text-[10px]">
                          {a.patient_unique_id}
                        </Badge>
                        {a.consultation_type && (
                          <Badge variant="secondary" className="text-[10px] hidden sm:inline-flex">
                            {a.consultation_type}
                          </Badge>
                        )}
                        {a.source === "online" && (
                          <Badge variant="outline" className="text-[10px] border-secondary text-secondary-foreground">
                            Online
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground truncate">
                        {a.reason || "Consultation"}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p
                        className={`font-heading text-base sm:text-lg font-semibold whitespace-nowrap ${
                          a.status === "completed" ? "text-emerald-700" : ""
                        }`}
                      >
                        {a.display_time || dayjs(a.scheduled_at).format("h:mm A")}
                      </p>
                      <span
                        data-testid={`appt-status-${a.id}`}
                        className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium capitalize border ${
                          a.status === "completed"
                            ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                            : a.status === "ongoing"
                            ? "bg-amber-100 text-amber-800 border-amber-200"
                            : a.status === "cancelled"
                            ? "bg-rose-100 text-rose-800 border-rose-200"
                            : "bg-slate-100 text-slate-700 border-slate-200"
                        }`}
                      >
                        {a.status}
                      </span>
                    </div>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => handleDeleteAppt(e, a)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") handleDeleteAppt(e, a);
                      }}
                      className="size-8 sm:size-9 rounded-full grid place-items-center text-rose-500 hover:bg-rose-50 hover:text-rose-700 transition-colors cursor-pointer shrink-0"
                      title="Delete appointment (no-show)"
                      data-testid={`delete-appt-${a.id}`}
                      aria-label="Delete appointment"
                    >
                      <Trash2 className="size-4" />
                    </span>
                    <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                  </div>
                </Card>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* ALL-TIME stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Patients (all)" value={stats?.patients} />
        <StatCard icon={HeartPulse} label="DHC patients (all)" value={stats?.chronic_total} />
      </div>

      {/* MONTH section */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold tracking-tight">
            {dayjs().format("MMMM YYYY")}
          </h3>
          <span className="text-xs uppercase tracking-widest text-secondary font-bold">
            This month
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={UserPlus} label="New patients" value={stats?.new_patients_month} />
          <StatCard icon={Repeat} label="Repeat patients" value={stats?.repeat_patients_month} />
          <StatCard icon={HeartPulse} label="DHC patients" value={stats?.dhc_month} />
        </div>
      </div>

      {/* LIBRARY section */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold tracking-tight">
            Content library
          </h3>
          <span className="text-xs uppercase tracking-widest text-secondary font-bold">
            Site content
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Activity} label="Diseases" value={stats?.diseases} />
          <StatCard icon={Pill} label="Medicines" value={stats?.medications} />
          <StatCard icon={Apple} label="Diet" value={stats?.diet_content} />
          <StatCard icon={Dumbbell} label="Exercise" value={stats?.exercise_content} />
        </div>
      </div>

      {/* Line chart */}
      <section data-testid="trends-section">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-heading text-2xl font-semibold tracking-tight">
            Monthly trends
          </h2>
          <span className="text-sm text-muted-foreground">Last 6 months</span>
        </div>
        <Card className="p-4">
          <div style={{ width: "100%", height: 320 }} data-testid="trends-chart">
            <ResponsiveContainer>
              <LineChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "white",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total_patients"
                  name="Total patients"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="new_patients"
                  name="New patients"
                  stroke="hsl(var(--secondary))"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="repeat_patients"
                  name="Repeat patients"
                  stroke="#B37B2A"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </section>

      {/* Earnings */}
      <section data-testid="earnings-section">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-heading text-2xl font-semibold tracking-tight">Earnings</h2>
          <span className="text-sm text-muted-foreground">₹ from consultation fees</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EarningCard
            icon={IndianRupee}
            label="Earned today"
            value={stats?.fees_today}
            subtext={`from ${stats?.appointments_today ?? 0} appointment(s)`}
            testId="earning-today"
          />
          <EarningCard
            icon={TrendingUp}
            label="Earned this month"
            value={stats?.fees_month}
            subtext={dayjs().format("MMMM YYYY")}
            testId="earning-month"
          />
        </div>
      </section>

      <AppointmentDetailModal
        appointment={selectedAppt}
        open={!!selectedAppt}
        onClose={() => setSelectedAppt(null)}
        onDeleted={() => {
          setSelectedAppt(null);
          load();
        }}
      />
    </div>
  );
}
